'use client';

import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { formatNumberLocalized } from '@shared/helpers/formatters';

import { tokensQueries } from '@api/token';
import { type MetaSchemaPayload } from '@api/token/coinInfo';

import { AdaptiveGallery } from '@ui/uikit/components/common/AdaptiveGallery';
import { InfoCard } from '@ui/uikit/components/common/InfoCard';
import { Typography } from '@ui/uikit/components/common/Typography';

type StatKey = Exclude<keyof MetaSchemaPayload, 'certikData'>;

interface StatConfigItem {
  key: StatKey;
  label: string;
  value?: number;
  currency?: string;
}

const statsConfig: StatConfigItem[] = [
  { key: 'marketCap', label: 'Market Cap', currency: '$' },
  { key: 'volume24h', label: 'Trading Volume', currency: '$' },
  { key: 'circulatingSupply', label: 'Circulating Supply' },
  { key: 'maxSupply', label: 'Max Supply' },
  { key: 'FDMC', label: 'FDMC', currency: '$' },
];

type AppTokenStatsCardsProps = {
  tokenIdOrSlug: string | number;
};

export const AppTokenStatsCards = ({ tokenIdOrSlug }: AppTokenStatsCardsProps) => {
  const { data } = useQuery(tokensQueries.coinInfo(tokenIdOrSlug));

  const stats = useMemo(
    () =>
      statsConfig.map((item) => ({
        ...item,
        value: data?.meta[item.key],
      })),
    [data?.meta],
  );

  return (
    <AdaptiveGallery
      numberOfSlides={2}
      spacing="8px"
      centerSlides={false}
      contentPadding={16}
      breakout
      breakpoints={{
        MEDIUM: {
          numberOfSlides: 5,
          contentPadding: 16,
        },
        LARGE: {
          numberOfSlides: 5,
          spacing: '16px',
          contentPadding: 16,
        },
        XLARGE: {
          numberOfSlides: 5,
          spacing: '16px',
          // contentPadding: 32,
        },
      }}
    >
      {stats.map((statItem) => {
        const hasValue = statItem.value !== undefined && statItem.value !== null;

        const formattedValue = hasValue
          ? `${statItem.currency ?? ''}${formatNumberLocalized(statItem.value, {
            options: { maximumFractionDigits: 0 },
          })}`
          : '—';

        return (
          <AdaptiveGallery.Slide key={statItem.key}>
            <InfoCard caption={statItem.label}>
              <Typography
                variant="caption-s-strong"
                isEllipsis
                title={formattedValue}
                isFullWidth
                color={hasValue ? 'primary' : 'quaternary'}
              >
                {formattedValue}
              </Typography>
            </InfoCard>
          </AdaptiveGallery.Slide>
        );
      })}
    </AdaptiveGallery>
  );
};
